import { visuallyHidden } from '@mui/utils'
import { makeStyles } from '@material-ui/core/styles'
import { PILL_BADGE_STYLES } from 'lib/styles/pill-badge'
import { RETENTION_STATUSES } from 'components/student-insights/constants'
import {
  ATTENTION_ROW_HEIGHT,
  ATTENTION_ROW_HEIGHT_COMPACT,
} from './attention-row-size'

const useStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: '#172024',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    minWidth: 0,
  },
  cardColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  emptyFill: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #E0E0E0',
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  cardHeaderRoomy: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.25),
    },
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  },
  cardHeading: {
    minWidth: 0,
  },
  iconCircleLarge: {
    width: 40,
    height: 40,
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  cardTitle: {
    color: '#172024',
  },
  cardSubtitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  tabs: {
    padding: theme.spacing(0.5, 3, 0),
  },
  tabsCompact: {
    padding: theme.spacing(0.5, 1, 0),
    '&& [role="tab"]': {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  visuallyHidden,
  avatar: {
    width: 48,
    height: 48,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '1rem',
    fontWeight: 600,
  },
  avatarCompact: {
    width: 40,
    height: 40,
    fontSize: '0.875rem',
  },
  leadChip: {
    ...PILL_BADGE_STYLES,
    flexShrink: 0,
    backgroundColor: RETENTION_STATUSES.NEW.background,
    color: RETENTION_STATUSES.NEW.color,
  },
  rowChevron: {
    marginLeft: theme.spacing(1),
    fontSize: 20,
    color: theme.palette.text.secondary,
  },
  cardAction: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8125rem',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
  },
  cardActionCompact: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  cardActionReserved: {
    visibility: 'hidden',
  },
  list: {
    padding: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    '& + &': {
      borderTop: '1px solid #EBF0F2',
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.02)',
    },
  },
  attentionBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  attentionRow: {
    gap: theme.spacing(2.5),
    padding: theme.spacing(2.5, 3),
    boxSizing: 'border-box',
    minHeight: ATTENTION_ROW_HEIGHT,
  },
  attentionRowCompact: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    minHeight: ATTENTION_ROW_HEIGHT_COMPACT,
  },
  upcomingRow: {
    padding: theme.spacing(1.5, 3),
  },
  rowMain: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  rowName: {
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    color: '#172024',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: 0,
    marginBottom: 2,
  },
  rowNameText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.blue.main,
    flexShrink: 0,
  },
  rowMeta: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
    margin: 0,
  },
  rowMetaSingleLine: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMetaClamped: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  rowTime: {
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    margin: 0,
  },
  rowSchedule: {
    textAlign: 'right',
  },
  rowDate: {
    fontSize: '0.8125rem',
    lineHeight: '18px',
    fontWeight: 500,
    color: '#172024',
    whiteSpace: 'nowrap',
    margin: 0,
  },
  rowTrailing: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  replyButton: {
    minWidth: 96,
    height: 36,
    fontSize: '0.875rem',
    borderRadius: 20,
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  replyButtonCompact: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50%',
  },
  compactButtonIcon: {
    fontSize: 20,
    // The message glyph sits about 3% above the middle of its viewBox.
    transform: 'translateY(0.6px)',
  },
  emptyList: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '18px',
  },
  emptyListFill: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

export default useStyles
